<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed z-30 inset-0 overflow-y-auto" @close="closeModal">
      <div
        class="
          flex
          items-end
          justify-center
          min-h-screen
          pt-4
          px-4
          pb-20
          text-center
          sm:block
          sm:p-0
        "
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="
              relative
              inline-block
              align-bottom
              bg-white
              rounded-lg
              px-4
              pt-5
              pb-4
              text-left
              overflow-hidden
              shadow-xl
              transform
              transition-all
              sm:my-8
              sm:align-middle
              sm:max-w-lg
              sm:w-full
              sm:p-6
            "
          >
            <div class="sm:flex sm:items-start">
              <div
                class="
                  mx-auto
                  flex-shrink-0 flex
                  items-center
                  justify-center
                  h-12
                  w-12
                  rounded-full
                  bg-red-100
                  sm:mx-0
                  sm:h-10
                  sm:w-10
                "
              >
                <ExclamationIcon class="h-6 w-6 text-red-600" aria-hidden="true" />
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900">
                  End session
                </DialogTitle>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    Are you sure you want to end the session? Press
                    <span class="font-bold">'Pause'</span>
                    if you plan to resume later.
                  </p>
                </div>
              </div>
            </div>
            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                class="
                  mt-3
                  w-full
                  inline-flex
                  justify-center
                  rounded-md
                  border border-gray-300
                  shadow-sm
                  px-4
                  py-2
                  bg-white
                  text-base
                  font-medium
                  text-gray-700
                  hover:bg-gray-50
                  focus:outline-none
                  focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                  sm:mt-0
                  sm:ml-3
                  sm:w-auto
                  sm:text-sm
                "
                @click="closeModal"
                ref="cancelButtonRef"
              >
                Cancel
              </button>
              <button
                type="button"
                class="
                  mt-3
                  w-full
                  inline-flex
                  justify-center
                  rounded-md
                  border border-gray-300
                  shadow-sm
                  px-4
                  py-2
                  bg-white
                  text-white
                  font-medium
                  focus:outline-none
                  focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                  bg-vice-rose-400
                  hover:bg-vice-rose-500
                  sm:mt-0
                  sm:ml-3
                  sm:w-auto
                  sm:text-sm
                "
                @click="openFinalModal"
                ref="continueButtonRef"
              >
                Continue
              </button>
              <button
                type="button"
                class="
                  mt-3
                  w-full
                  inline-flex
                  justify-center
                  items-center
                  rounded-md
                  border border-gray-300
                  shadow-sm
                  px-4
                  py-2
                  bg-white
                  text-base
                  font-medium
                  text-gray-700
                  hover:bg-gray-50
                  focus:outline-none
                  focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                  sm:mt-0
                  sm:ml-3
                  sm:w-auto
                  sm:text-sm
                "
                @click="togglePausedState(true)"
                ref="pauseButtonRef"
              >
                <PauseIcon
                  :class="{ 'opacity-0': sessionStore.togglingPausedState }"
                  class="w-2 h-2 mr-1 text-gray-500"
                />
                Pause
              </button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { ExclamationIcon } from '@heroicons/vue/outline';
import useSessionStore from '@/Stores/useSessionStore';
import PauseIcon from '@/Components/Icons/PauseIcon.vue';

export default {
  components: {
    PauseIcon,
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    ExclamationIcon,
  },
  emits: ['closeEndSessionModal', 'openEndSessionFinalModal'],
  props: {
    open: {
      type: Boolean,
    },
  },
  setup(_props, { emit }) {
    const sessionStore = useSessionStore();
    const closeModal = () => {
      emit('closeEndSessionModal');
    };

    const openFinalModal = () => {
      emit('openEndSessionFinalModal');
      closeModal();
    };

    const togglePausedState = (isPaused) => {
      sessionStore.togglePausedState(isPaused);
      closeModal();
    };

    return {
      sessionStore,
      closeModal,
      openFinalModal,
      togglePausedState,
    };
  },
};
</script>
